import React from 'react';
import { Helmet } from 'react-helmet';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import Icon from 'components/AppIcon';
import { Link } from 'react-router-dom';

const DigitalMarketingServicesPage = () => {
  const marketingModules = [
    {
      icon: 'Search',
      title: 'Stratégie SEO/SEA avancée',
      description: 'Dominez les moteurs de recherche avec des stratégies SEO et SEA performantes pour maximiser votre visibilité en ligne.',
      features: [
        'Audit SEO technique et sémantique complet',
        'Optimisation on-page et off-page avancée',
        'Campagnes Google Ads et Bing Ads optimisées',
        'Suivi des performances et optimisation continue',
        'Analyse concurrentielle et positionnement'
      ]
    },
    {
      icon: 'Share2',
      title: 'Marketing des médias sociaux B2B/B2C',
      description: 'Construisez une présence sociale forte et engagez votre audience sur tous les canaux digitaux.',
      features: [
        'Stratégie social media multicanale personnalisée',
        'Création de contenu engageant et viral',
        'Community management professionnel',
        'Publicité sociale (Facebook, LinkedIn, Instagram, Twitter)',
        'Analyse des performances et ROI social'
      ]
    },
    {
      icon: 'Database',
      title: 'CRM et automatisation marketing',
      description: 'Optimisez votre relation client et automatisez vos campagnes marketing pour un ROI maximal.',
      features: [
        'Mise en place et paramétrage CRM (HubSpot, Salesforce)',
        'Marketing automation et workflows intelligents',
        'Lead nurturing et scoring avancé',
        'Email marketing et segmentation comportementale',
        'Intégration multicanale et reporting'
      ]
    },
    {
      icon: 'Target',
      title: 'Plan marketing digital en 9 étapes',
      description: 'Méthodologie complète et éprouvée pour construire et déployer votre stratégie marketing digitale avec succès.',
      features: [
        'Analyse de marché et audit concurrentiel',
        'Définition des personas et parcours client',
        'Stratégie de contenu et calendrier éditorial',
        'Mix marketing digital et allocation budgétaire',
        'KPIs, mesure de performance et optimisation continue'
      ]
    }
  ];

  const whyChooseUs = [
    {
      icon: 'TrendingUp',
      title: 'Résultats Mesurables',
      description: 'Stratégies data-driven avec suivi des KPIs et optimisation continue pour maximiser votre ROI digital.'
    },
    {
      icon: 'Award',
      title: 'Experts Certifiés',
      description: 'Consultants certifiés Google Ads, Facebook Blueprint, HubSpot avec expérience terrain internationale.'
    },
    {
      icon: 'Zap',
      title: 'Approche 360°',
      description: 'Vision globale du marketing digital : SEO/SEA, Social Media, Content Marketing, CRM et Analytics.'
    },
    {
      icon: 'Users',
      title: 'Formation Pratique',
      description: 'Pédagogie active avec cas réels, ateliers pratiques et accompagnement post-formation personnalisé.'
    }
  ];

  const aiInMarketing = [
    {
      icon: 'Brain',
      title: 'IA appliquée au marketing',
      description: 'Intégrez l\'Intelligence Artificielle dans vos stratégies marketing pour une performance optimale.',
      applications: [
        'Personnalisation avancée des campagnes avec IA',
        'Chatbots et assistants virtuels intelligents',
        'Analyse prédictive et scoring comportemental',
        'Génération de contenu avec IA générative',
        'Optimisation automatique des budgets publicitaires'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Marketing Digital | CRF PERFECTION - Formations & Stratégies Digitales 2026</title>
        <meta 
          name="description" 
          content="Formations marketing digital 2026 : SEO/SEA avancé, médias sociaux B2B/B2C, CRM, automatisation marketing, plan digital en 9 étapes et IA appliquée au marketing."
        />
        <meta name="keywords" content="marketing digital, formation SEO, formation SEA, réseaux sociaux, CRM, automatisation marketing, IA marketing, stratégie digitale 2026" />
        <meta property="og:title" content="Marketing Digital | CRF PERFECTION" />
        <meta 
          property="og:description" 
          content="Maîtrisez le marketing digital avec nos formations 2026 : SEO/SEA, Social Media, CRM, Automatisation et IA."
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        {/* Section 1: Hero avec Introduction */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(155, 142, 87, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(155, 142, 87, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }} />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                  <Icon name="TrendingUp" size={20} style={{ color: '#9B8E57' }} />
                  <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                    Pôle Marketing Digital & Innovation
                  </span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  Marketing Digital
                </h1>
                
                <p className="text-xl md:text-2xl font-semibold" style={{ color: '#9B8E57' }}>
                  Propulsez votre croissance digitale avec des stratégies innovantes et data-driven
                </p>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-lg font-bold mb-3" style={{ color: '#9B8E57' }}>Catalogue de Formation 2026</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Dans un monde où la présence digitale est devenue incontournable, maîtriser le marketing digital 
                    n'est plus une option mais une nécessité stratégique. Nos formations vous permettent de dominer 
                    les canaux digitaux et de maximiser votre ROI.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    <strong>Du SEO/SEA aux médias sociaux, du CRM à l'automatisation marketing, jusqu'à l'intégration de l'IA</strong> : développez une expertise complète pour piloter votre transformation digitale.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                    style={{ 
                      backgroundColor: '#96121D',
                      boxShadow: '0 4px 14px rgba(150, 18, 29, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#7d0f18';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#96121D';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Demander un Devis
                    <Icon name="ArrowRight" size={20} className="ml-2" />
                  </Link>
                  
                  <Link 
                    to="/service-request-form"
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 border-2"
                    style={{ 
                      borderColor: '#9B8E57',
                      color: '#9B8E57'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#9B8E57';
                      e.target.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#9B8E57';
                    }}
                  >
                    Télécharger le Catalogue 2026
                    <Icon name="Download" size={20} className="ml-2" />
                  </Link>
                </div>
              </div>

              {/* Right Stats */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#9B8E57' }}>300%</div>
                  <p className="text-gray-300">Augmentation moyenne du trafic web</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#9B8E57' }}>85%</div>
                  <p className="text-gray-300">Amélioration des conversions</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#9B8E57' }}>4</div>
                  <p className="text-gray-300">Modules de formation spécialisés</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#9B8E57' }}>+IA</div>
                  <p className="text-gray-300">Intelligence Artificielle intégrée</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Modules de Formation Marketing Digital */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 bg-gray-100 rounded-full px-4 py-2 mb-4">
                <Icon name="Laptop" size={20} style={{ color: '#96121D' }} />
                <span className="text-sm font-semibold" style={{ color: '#96121D' }}>
                  Pôle Projets Numériques & Innovation
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Modules de Formation Marketing Digital
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Maîtrisez les leviers essentiels du marketing digital pour propulser votre croissance en ligne
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {marketingModules?.map((module, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-opacity-100 transition-all duration-300 hover:shadow-xl"
                  style={{ borderColor: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                >
                  <div 
                    className="w-16 h-16 rounded-lg flex items-center justify-center mb-6"
                    style={{ backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)' }}
                  >
                    <Icon name={module.icon} size={32} style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{module.title}</h3>
                  <p className="text-gray-600 mb-6">{module.description}</p>
                  <ul className="space-y-3">
                    {module.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Icon 
                          name="CheckCircle" 
                          size={20} 
                          className="mr-3 mt-0.5 flex-shrink-0" 
                          style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                        />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* IA appliquée au Marketing */}
            <div className="mt-12">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 text-white">
                <div className="flex items-center mb-6">
                  <div 
                    className="w-16 h-16 rounded-lg flex items-center justify-center mr-6"
                    style={{ backgroundColor: 'rgba(155, 142, 87, 0.2)' }}
                  >
                    <Icon name="Brain" size={32} style={{ color: '#9B8E57' }} />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-2">IA appliquée au marketing</h3>
                    <p className="text-gray-300">
                      Intégrez l'Intelligence Artificielle dans vos stratégies marketing pour une performance optimale
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {aiInMarketing?.[0]?.applications?.map((app, idx) => (
                    <div key={idx} className="flex items-start bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <Icon name="Sparkles" size={20} className="mr-3 mt-0.5 flex-shrink-0" style={{ color: '#9B8E57' }} />
                      <span className="text-gray-200">{app}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Pourquoi Choisir CRF Perfection */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 bg-white rounded-full px-4 py-2 mb-4 shadow-sm">
                <Icon name="Award" size={20} style={{ color: '#96121D' }} />
                <span className="text-sm font-semibold" style={{ color: '#96121D' }}>
                  Excellence & Expertise
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Pourquoi Choisir CRF Perfection ?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Un partenaire de croissance pour votre transformation digitale
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs?.map((reason, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4"
                  style={{ borderTopColor: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                >
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto"
                    style={{ backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)' }}
                  >
                    <Icon name={reason?.icon} size={28} style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{reason?.title}</h3>
                  <p className="text-gray-600 text-center">{reason?.description}</p>
                </div>
              ))}
            </div>

            {/* Engagement CRF Perfection */}
            <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Notre Engagement : Plus qu'un cabinet, un partenaire de croissance
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: 'rgba(150, 18, 29, 0.1)' }}>
                    <Icon name="Briefcase" size={24} style={{ color: '#96121D' }} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Expertise Terrain</h4>
                  <p className="text-gray-600">Nos formateurs sont des consultants actifs, confrontés aux réalités du terrain</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: 'rgba(155, 142, 87, 0.1)' }}>
                    <Icon name="Settings" size={24} style={{ color: '#9B8E57' }} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Personnalisation</h4>
                  <p className="text-gray-600">Chaque parcours est conçu sur mesure selon vos enjeux spécifiques</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: 'rgba(150, 18, 29, 0.1)' }}>
                    <Icon name="TrendingUp" size={24} style={{ color: '#96121D' }} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Vision Marché</h4>
                  <p className="text-gray-600">Nos formations sont alignées sur les compétences réellement recherchées</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: CTA Final */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Icon name="Rocket" size={20} style={{ color: '#9B8E57' }} />
              <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                Prêt à Transformer Votre Marketing Digital ?
              </span>
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Bâtissons ensemble votre stratégie digitale 2026
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Contactez-nous dès aujourd'hui pour discuter de vos besoins en formation marketing digital 
              et découvrir comment nous pouvons propulser votre croissance en ligne.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                style={{ 
                  backgroundColor: '#96121D',
                  boxShadow: '0 4px 14px rgba(150, 18, 29, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#7d0f18';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#96121D';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Demander une Consultation
                <Icon name="ArrowRight" size={20} className="ml-2" />
              </Link>
              
              <Link 
                to="/service-request-form"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 border-2"
                style={{ 
                  borderColor: '#9B8E57',
                  color: '#9B8E57'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#9B8E57';
                  e.target.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#9B8E57';
                }}
              >
                Télécharger le Catalogue
                <Icon name="Download" size={20} className="ml-2" />
              </Link>
            </div>

            <div className="mt-12 pt-8 border-t border-white/20">
              <p className="text-gray-400 mb-4">
                <strong style={{ color: '#9B8E57' }}>CRF Perfection</strong> – Bâtissons ensemble les compétences de demain.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-400">
                <div className="flex items-center">
                  <Icon name="Phone" size={16} className="mr-2" />
                  +1 240-978-4155
                </div>
                <div className="flex items-center">
                  <Icon name="Mail" size={16} className="mr-2" />
                  contact@crfperfection.pro
                </div>
                <div className="flex items-center">
                  <Icon name="MapPin" size={16} className="mr-2" />
                  Washington D.C.
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default DigitalMarketingServicesPage;